'use client';
import { useContext, useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { ProjectSettings } from 'shared/types/projectSettings/ProjectSettings';
import { hideMakeAPaymentButton } from '@/components/customercenter-ui/utils/assetSaleUtils';
import { useCheckout } from '@/context/checkoutProvider';
import { UrlHistory } from '@/context/historyTrackerProvider';
import { useAccount, useCart } from '@/frontastic';
import { useProjSettings } from '@/frontastic/hooks/useProjectSettings/useProjectSettings';
import { useShowActiveBrandSection } from '@/frontastic/hooks/useShowActiveBrandSection';
import { getAgentName, getCookieValue, getSelectedDivision, isCreditSite, PathNames } from '@/helpers/utils/siteUtils';
import { PhoneRecordingPayload } from '@/sdk/composable-commerce/types/payloads/AccountPayloads';
import { COOKIE_KEYS, NO_CHARGE_ORDER, ORDER_TYPES } from '../../../../../../constants/constant';
import MakePaymentModal from '../../../modals/customerDetailAction/makeAPayment';
import NewChargeOrderModal from '../../../modals/customerDetailAction/newChargeOrderModal';
import NewMailOrderModal from '../../../modals/customerDetailAction/newMailOrderModal';
import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/card';
import Loader from '../../../ui/loader';
import { Customer, ShippingAddressDetailsType } from '../../types';

const CustomerAccountActions = ({
  Texts,
  customerDetails,
  primaryEmail,
  creditAccountId,
  billingAddress,
}: {
  Texts: any;
  customerDetails: Customer;
  primaryEmail: string;
  creditAccountId: string;
  billingAddress: ShippingAddressDetailsType;
}) => {
  const router = useRouter();
  const projectSettings: ProjectSettings = useProjSettings();
  const showActiveBrandSection = useShowActiveBrandSection();
  const { resetContextValues } = useCheckout();
  const [loading, setLoading] = useState(false);
  const [isClientSide, setIsClientSide] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const cssClass = `items-center max-w-[300px] h-[40px] border-[1px] border-foggyGray bg-lightSilver py-1.5 text-lg font-medium rounded-md`;
  const { updatePageName } = useContext(UrlHistory);
  const { startPhoneRecording, stopPhoneRecording } = useAccount();
  const { resetCart } = useCart(false);
  const creditSite = isCreditSite();
  const isSoldAccount = customerDetails?.DivisionCustomers?.[0]?.CreditAccount?.AccountInfo?.isSoldAccount ?? false;
  const { allowedPermissionsForAgent } = projectSettings;

  const redirectToPlp = async (name: string, orderType?: string) => {
    setLoading(true);
    resetContextValues();
    const divisionCode = getSelectedDivision() ?? 'ZB';
    await resetCart({
      orderType: orderType,
      omsCustomerId: customerDetails?.customerId,
      divisionCode: divisionCode,
    });
    updatePageName(name, PathNames.SEARCH);
    router.push('/search');
  };

  const startRecording = async () => {
    const payload = {
      data: {
        agentEmailAddress: getAgentName(),
        divisionCode: getCookieValue(COOKIE_KEYS?.selectedSite)?.slice(0, 2) ?? '',
      },
    } as PhoneRecordingPayload;
    await startPhoneRecording(payload);
  };

  const stopRecording = async () => {
    const payload = {
      data: {
        agentEmailAddress: getAgentName(),
        divisionCode: getCookieValue(COOKIE_KEYS?.selectedSite)?.slice(0, 2) ?? '',
      },
    } as PhoneRecordingPayload;
    await stopPhoneRecording(payload);
  };

  useEffect(() => {
    setIsClientSide(true);
  }, []);

  return (
    showActiveBrandSection && (
      <Card className="sticky top-0 z-10 mb-4 rounded-md border-[1px] border-foggyGray bg-white shadow-lg">
        {loading && <Loader />}
        <CardHeader className="p-5">
          <CardTitle className="text-lg font-bold">{Texts.actions}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <button
            onClick={async () => {
              setLoading(true);
              resetContextValues();
              const divisionCode = getSelectedDivision() ?? 'ZB';
              await resetCart({
                orderType: ORDER_TYPES.NEW_ORDER,
                omsCustomerId: customerDetails?.customerId,
                divisionCode: divisionCode,
              });
              updatePageName(Texts.createNewOrder, PathNames.SEARCH);
              router.push('/search');
            }}
            className={`${cssClass} flex items-center justify-center`}
          >
            {Texts.createNewOrder}
          </button>
          {isClientSide && (
            <NewMailOrderModal
              Texts={Texts}
              redirectToPlp={() => redirectToPlp(Texts.beginMailOrder, ORDER_TYPES.MAIL_ORDER)}
            />
          )}
          {allowedPermissionsForAgent?.includes(NO_CHARGE_ORDER) && isClientSide && (
            <NewChargeOrderModal
              Texts={Texts}
              redirectToPlp={() => redirectToPlp(Texts.noChargeOrder, ORDER_TYPES.NO_CHARGE)}
            />
          )}
          {!hideMakeAPaymentButton(projectSettings, isSoldAccount) && creditSite && isClientSide ? (
            <button
              className={cssClass}
              onClick={() => {
                setOpenModal(true);
                stopRecording();
              }}
            >
              {Texts.makeAPayment}
            </button>
          ) : (
            <></>
          )}
          {openModal && (
            <MakePaymentModal
              open={openModal}
              setOpen={setOpenModal}
              Texts={Texts}
              customerDetails={customerDetails}
              primaryEmail={primaryEmail}
              creditAccountId={creditAccountId}
              billingAddress={billingAddress}
              startRecording={startRecording}
            />
          )}
        </CardContent>
      </Card>
    )
  );
};
export default CustomerAccountActions;
